import React from 'react'
import { Col, Row, Button, Card, CardBody, Badge } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import FalconModal from '../../components/Inputs/FalconModal'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import { useNavigate } from 'react-router-dom'

import {
  _surveyClass,
  _surveyUISchema,
  _surveyQuestionTypes,
  SURVEY_STATE_TRANSLATIONS,
  SURVEY_STATE_COLORS,
} from '../../components/Inputs/schemas/survey'
import {
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import ScheduleIcon from '@mui/icons-material/Schedule'
import LinkIcon from '@mui/icons-material/Link'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import { withAuthentication } from '../../components/Session'

function OptionsInput({ options, onChange }) {
  const [localOptions, setLocalOptions] = React.useState(options || [])

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...localOptions]
    updatedOptions[index] = value
    setLocalOptions(updatedOptions)
    onChange(updatedOptions)
  }

  const handleAddOption = () => {
    const updatedOptions = [...localOptions, '']
    setLocalOptions(updatedOptions)
    onChange(updatedOptions)
  }

  const handleRemoveOption = (index) => {
    const updatedOptions = localOptions.filter((_, i) => i !== index)
    setLocalOptions(updatedOptions)
    onChange(updatedOptions)
  }

  return (
    <div>
      {localOptions.map((option, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <TextField
            label={`Vaihtoehto ${index + 1}`}
            value={option}
            onChange={(e) => handleOptionChange(index, e.target.value)}
            size="small"
            style={{ marginRight: 8 }}
          />
          <IconButton size="small" onClick={() => handleRemoveOption(index)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button size="sm" onClick={handleAddOption}>
        Lisää vaihtoehto
      </Button>
    </div>
  )
}

function SurveyDesigner({ survey, onSave, onClose }) {
  const [localSurvey, setLocalSurvey] = React.useState(survey || { inputs: [] })
  const [editedInputs, setEditedInputs] = React.useState(survey?.inputs || [])
  const [isEdited, setIsEdited] = React.useState(false)

  const handleSurveyChange = (field, value) => {
    setLocalSurvey({ ...localSurvey, [field]: value })
    setIsEdited(true)
  }

  const handleInputChange = (index, field, value) => {
    const updatedInputs = [...editedInputs]
    updatedInputs[index] = { ...updatedInputs[index], [field]: value }
    setEditedInputs(updatedInputs)
    setIsEdited(true)
  }

  const handleAddInput = () => {
    setEditedInputs([...editedInputs, { label: '', type: 'text', required: false }])
    setIsEdited(true)
  }

  const handleRemoveInput = (index) => {
    setEditedInputs(editedInputs.filter((_, i) => i !== index))
    setIsEdited(true)
  }

  const handleMoveUp = (index) => {
    if (index === 0) return
    const updatedInputs = [...editedInputs]
    const temp = updatedInputs[index - 1]
    updatedInputs[index - 1] = updatedInputs[index]
    updatedInputs[index] = temp
    setEditedInputs(updatedInputs)
    setIsEdited(true)
  }

  const handleMoveDown = (index) => {
    if (index === editedInputs.length - 1) return
    const updatedInputs = [...editedInputs]
    const temp = updatedInputs[index + 1]
    updatedInputs[index + 1] = updatedInputs[index]
    updatedInputs[index] = temp
    setEditedInputs(updatedInputs)
    setIsEdited(true)
  }

  const handleSave = () => {
    const surveyToSave = { ...localSurvey, inputs: editedInputs }
    onSave(surveyToSave)
    setIsEdited(false)
  }

  const handleClose = () => {
    if (isEdited) {
      if (window.confirm('Sinulla on tallentamattomia muutoksia. Haluatko varmasti poistua?')) {
        onClose()
      }
    } else {
      onClose()
    }
  }

  const needsOptions = (type) => {
    return ['select', 'multiselect', 'radio', 'checkbox'].includes(type)
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Back button */}
      <div style={{ marginBottom: 16 }}>
        <Button color="secondary" onClick={handleClose}>
          <ArrowBackIcon style={{ marginRight: 4 }} /> Takaisin
        </Button>
      </div>

      {/* Survey parameters */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Kyselyn tiedot</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Kyselyn nimi"
              value={localSurvey.name || ''}
              onChange={(e) => handleSurveyChange('name', e.target.value)}
              fullWidth
              required
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Aloituspäivä"
              type="date"
              value={localSurvey.startDate || ''}
              onChange={(e) => handleSurveyChange('startDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Päättymispäivä"
              type="date"
              value={localSurvey.deadline || ''}
              onChange={(e) => handleSurveyChange('deadline', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localSurvey.anonymous || false}
                  onChange={(e) => handleSurveyChange('anonymous', e.target.checked)}
                  size="small"
                />
              }
              label="Anonyymi"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localSurvey.showOnUserUI || false}
                  onChange={(e) => handleSurveyChange('showOnUserUI', e.target.checked)}
                  size="small"
                />
              }
              label="Näytä asiakkaille"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Kuvaus"
              value={localSurvey.description || ''}
              onChange={(e) => handleSurveyChange('description', e.target.value)}
              multiline
              rows={5}
              style={{ minWidth: 300 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Questions section */}
      <Typography variant="h6" style={{ marginBottom: 12 }}>
        Kysymykset ({editedInputs.length})
      </Typography>

      {editedInputs.map((input, index) => (
        <Paper key={index} style={{ padding: 16, marginBottom: 12 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Order buttons */}
            <Grid item xs="auto" style={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton
                size="small"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                title="Siirrä ylös"
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" style={{ textAlign: 'center' }}>
                {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleMoveDown(index)}
                disabled={index === editedInputs.length - 1}
                title="Siirrä alas"
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Grid>

            {/* Question content */}
            <Grid item xs>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Kysymys"
                    value={input.label || ''}
                    onChange={(e) => handleInputChange(index, 'label', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tyyppi</InputLabel>
                    <Select
                      value={input.type || 'text'}
                      label="Tyyppi"
                      onChange={(e) => handleInputChange(index, 'type', e.target.value)}
                    >
                      {_surveyQuestionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={input.required || false}
                        onChange={(e) => handleInputChange(index, 'required', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Pakollinen"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Lyhyt kuvaus (valinnainen)"
                    value={input.description || ''}
                    onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                    fullWidth
                    placeholder="Lisäohje tai selite kysymykselle"
                    style={{ minWidth: 400 }}
                  />
                </Grid>
                {needsOptions(input.type) && (
                  <Grid item xs={12}>
                    <OptionsInput
                      options={input.options || []}
                      onChange={(options) => handleInputChange(index, 'options', options)}
                    />
                  </Grid>
                )}
                {input.type === 'paragraph' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Ohjeteksti"
                      value={input.properties?.content || ''}
                      onChange={(e) =>
                        handleInputChange(index, 'properties', {
                          ...input.properties,
                          content: e.target.value,
                        })
                      }
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Delete button at far right */}
            <Grid item xs="auto" style={{ display: 'flex', alignItems: 'flex-start', marginLeft: 'auto' }}>
              <IconButton onClick={() => handleRemoveInput(index)} color="error" title="Poista">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Bottom row: Add question and Save buttons */}
      <Paper style={{ padding: 16, marginTop: 16 }}>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item>
            <Button color="primary" onClick={handleAddInput}>
              <AddIcon style={{ marginRight: 4 }} /> Lisää kysymys
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="success"
              onClick={handleSave}
              disabled={!isEdited}
            >
              <SaveIcon style={{ marginRight: 4 }} /> Tallenna
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  )
}

function SurveyListItem({ survey, onEdit, onDelete, onActivate, onClose, onViewResults, onCopy, disabled }) {
  const [copied, setCopied] = React.useState(false)
  const stateColor = SURVEY_STATE_COLORS[survey.state] || 'secondary'
  const stateLabel = SURVEY_STATE_TRANSLATIONS[survey.state] || survey.state

  const surveyLink = `https://links.cintoia.com/s/${survey.id}`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(surveyLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Card>
      <CardBody>
        <Row>
          <Col>
            <h4>
              {survey.name}{' '}
              <Badge color={stateColor}>{stateLabel}</Badge>
              {!survey.showOnUserUI && survey.state !== 'draft' && (
                <Badge color="info" style={{ marginLeft: 4 }} title="Vain suoran linkin kautta">
                  <LinkIcon fontSize="small" style={{ fontSize: 12 }} />
                </Badge>
              )}
            </h4>
            <p className="text-muted">{survey.description}</p>
          </Col>
          <Col xs="auto" className="text-right">
            {(survey.state === 'draft' || survey.state === 'scheduled') && (
              <Button
                disabled={disabled}
                color="success"
                size="sm"
                onClick={() => onActivate(survey)}
                title="Aktivoi"
              >
                <PlayArrowIcon fontSize="small" /> Aktivoi
              </Button>
            )}
            {survey.state === 'active' && (
              <Button
                disabled={disabled}
                color="warning"
                size="sm"
                onClick={() => onClose(survey.id)}
                title="Sulje"
              >
                <StopIcon fontSize="small" /> Sulje
              </Button>
            )}
            <Button
              disabled={disabled}
              size="sm"
              onClick={() => onViewResults(survey.id)}
              title="Näytä tulokset"
              style={{ marginLeft: 4 }}
            >
              <VisibilityIcon fontSize="small" /> Tulokset
            </Button>
            <Button
              disabled={disabled || survey.state === 'active' || survey.state === 'closed'}
              size="sm"
              onClick={() => onEdit(survey.id)}
              title="Muokkaa"
              style={{ marginLeft: 4 }}
            >
              <EditIcon fontSize="small" />
            </Button>
            <Button
              disabled={disabled || survey.state === 'active'}
              size="sm"
              onClick={() => onCopy(survey)}
              title="Kopioi kysely"
              style={{ marginLeft: 4 }}
            >
              <ContentCopyIcon fontSize="small" />
            </Button>
            <Button
              disabled={disabled || survey.state === 'active'}
              color="danger"
              size="sm"
              onClick={() => onDelete(survey.id)}
              title="Poista"
              style={{ marginLeft: 4 }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={6}>
            <Typography variant="caption">
              Kysymyksiä: {survey.inputs?.length || 0}
            </Typography>
          </Col>
          <Col xs={6}>
            <Typography variant="caption">
              Vastauksia: {survey.responseCount || 0}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            {survey.startDate && (
              <Typography variant="caption">
                <ScheduleIcon fontSize="small" style={{ fontSize: 14, marginRight: 4 }} />
                Alkaa: {moment(survey.startDate).format('D.M.YYYY')}
              </Typography>
            )}
          </Col>
          <Col xs={6}>
            {survey.deadline && (
              <Typography variant="caption">
                Päättyy: {moment(survey.deadline).format('D.M.YYYY')}
              </Typography>
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            <Typography variant="caption">
              Luotu: {survey.created?.toDate ? moment(survey.created.toDate()).format('D.M.YYYY HH:mm') : '-'}
            </Typography>
            {!survey.showOnUserUI && (
              <Typography variant="caption" style={{ marginLeft: 8 }}>
                | Vain linkillä
              </Typography>
            )}
          </Col>
        </Row>
        <Row className="mt-2" style={{ alignItems: 'center' }}>
          <Col xs="auto">
            <Button
              size="sm"
              color={copied ? 'success' : 'secondary'}
              onClick={handleCopyLink}
              title={surveyLink}
            >
              <ContentCopyIcon fontSize="small" style={{ marginRight: 4 }} />
              {copied ? 'Kopioitu!' : 'Kopioi linkki'}
            </Button>
          </Col>
          <Col>
            <Typography variant="caption" style={{ color: '#666' }}>
              {surveyLink}
            </Typography>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

class SurveysContainerClass extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      surveys: [],
      selectedSurvey: null,
      modalOpen: false,
      designerOpen: false,
      processing: false,
      activateDialogOpen: false,
      surveyToActivate: null,
    }
  }

  componentDidMount() {
    this.loadSurveys()
  }

  loadSurveys = async () => {
    this.setState({ loading: true })
    try {
      const surveys = await this.props.firebase.getSurveys()
      this.setState({ surveys, loading: false })
    } catch (error) {
      console.error('Error loading surveys:', error)
      this.setState({ loading: false })
    }
  }

  getSurveySchema = () => {
    return {
      type: 'object',
      required: ['name'],
      properties: JSON.parse(JSON.stringify(_surveyClass)),
    }
  }

  handleOpenModal = (surveyId = null) => {
    if (surveyId) {
      const survey = this.state.surveys.find((s) => s.id === surveyId)
      this.setState({ selectedSurvey: survey, modalOpen: true })
    } else {
      this.setState({ selectedSurvey: {}, modalOpen: true })
    }
  }

  handleCloseModal = () => {
    this.setState({ modalOpen: false, selectedSurvey: null })
  }

  handleOpenDesigner = (surveyIdOrObject) => {
    if (typeof surveyIdOrObject === 'object') {
      // Direct survey object passed
      this.setState({ selectedSurvey: surveyIdOrObject, designerOpen: true })
    } else {
      // Survey ID passed, find in state
      const survey = this.state.surveys.find((s) => s.id === surveyIdOrObject)
      this.setState({ selectedSurvey: survey, designerOpen: true })
    }
  }

  handleCloseDesigner = () => {
    this.setState({ designerOpen: false, selectedSurvey: null })
  }

  handleSaveSurvey = async (survey) => {
    this.setState({ processing: true })
    try {
      if (survey.id) {
        await this.props.firebase.updateSurvey(survey)
      } else {
        await this.props.firebase.createSurvey(survey)
      }
      await this.loadSurveys()
      this.handleCloseDesigner()
      this.handleCloseModal()
    } catch (error) {
      console.error('Error saving survey:', error)
    }
    this.setState({ processing: false })
  }

  handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän kyselyn?')) return
    this.setState({ processing: true })
    try {
      await this.props.firebase.deleteSurvey(surveyId)
      await this.loadSurveys()
    } catch (error) {
      console.error('Error deleting survey:', error)
    }
    this.setState({ processing: false })
  }

  handleActivateSurvey = (survey) => {
    this.setState({ activateDialogOpen: true, surveyToActivate: survey })
  }

  handleCloseActivateDialog = () => {
    this.setState({ activateDialogOpen: false, surveyToActivate: null })
  }

  handleActivateNow = async () => {
    const { surveyToActivate } = this.state
    if (!surveyToActivate) return
    this.setState({ processing: true, activateDialogOpen: false })
    try {
      await this.props.firebase.activateSurvey(surveyToActivate.id)
      await this.loadSurveys()
    } catch (error) {
      console.error('Error activating survey:', error)
    }
    this.setState({ processing: false, surveyToActivate: null })
  }

  handleScheduleSurvey = async () => {
    const { surveyToActivate } = this.state
    if (!surveyToActivate || !surveyToActivate.startDate) return
    this.setState({ processing: true, activateDialogOpen: false })
    try {
      await this.props.firebase.scheduleSurvey(surveyToActivate.id, surveyToActivate.startDate)
      await this.loadSurveys()
    } catch (error) {
      console.error('Error scheduling survey:', error)
    }
    this.setState({ processing: false, surveyToActivate: null })
  }

  handleCloseSurvey = async (surveyId) => {
    this.setState({ processing: true })
    try {
      await this.props.firebase.closeSurvey(surveyId)
      await this.loadSurveys()
    } catch (error) {
      console.error('Error closing survey:', error)
    }
    this.setState({ processing: false })
  }

  handleCopySurvey = async (survey) => {
    this.setState({ processing: true })
    try {
      const newSurvey = {
        name: `${survey.name} (kopio)`,
        description: '',
        inputs: JSON.parse(JSON.stringify(survey.inputs || [])), // Deep copy
        anonymous: survey.anonymous || false,
        showOnUserUI: survey.showOnUserUI || false,
        startDate: '',
        deadline: '',
      }
      const result = await this.props.firebase.createSurvey(newSurvey)
      const createdSurvey = await this.props.firebase.getSurvey(result.id)
      await this.loadSurveys()
      this.handleOpenDesigner(createdSurvey)
    } catch (error) {
      console.error('Error copying survey:', error)
    }
    this.setState({ processing: false })
  }

  handleViewResults = (surveyId) => {
    this.props.navigate(`/admin/messaging/surveys/${surveyId}/results`)
  }

  render() {
    const { loading, surveys, processing, designerOpen, selectedSurvey, modalOpen, activateDialogOpen, surveyToActivate } = this.state

    if (loading) {
      return (
        <div className="content">
          <Row>
            <Col className="text-center">
              <CircularProgress />
              <Typography>Ladataan kyselyitä...</Typography>
            </Col>
          </Row>
        </div>
      )
    }

    if (designerOpen) {
      return (
        <div className="content">
          <SurveyDesigner
            survey={selectedSurvey}
            onSave={this.handleSaveSurvey}
            onClose={this.handleCloseDesigner}
          />
        </div>
      )
    }

    return (
      <div className="content">
        <Row>
          <Col>
            <Button
              disabled={processing}
              color="primary"
              onClick={() => this.handleOpenModal()}
            >
              Luo uusi kysely
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Typography variant="caption">
              Kyselyt mahdollistavat asiakaspalautteen keräämisen. Luo kysely, lisää kysymykset ja aktivoi se vastaanottaaksesi vastauksia.
            </Typography>
          </Col>
        </Row>
        <Row className="mt-3">
          {surveys.length === 0 ? (
            <Col>
              <Typography>Ei kyselyitä. Luo ensimmäinen kysely yllä olevasta napista.</Typography>
            </Col>
          ) : (
            surveys.map((survey) => (
              <Col xs="12" md="6" key={survey.id} className="mb-3">
                <SurveyListItem
                  survey={survey}
                  disabled={processing}
                  onEdit={this.handleOpenDesigner}
                  onDelete={this.handleDeleteSurvey}
                  onActivate={this.handleActivateSurvey}
                  onClose={this.handleCloseSurvey}
                  onViewResults={this.handleViewResults}
                  onCopy={this.handleCopySurvey}
                />
              </Col>
            ))
          )}
        </Row>

        <FalconModal
          onCreate={async (data) => {
            const result = await this.props.firebase.createSurvey(data)
            // Fetch the created survey and open designer with it
            const createdSurvey = await this.props.firebase.getSurvey(result.id)
            this.handleOpenDesigner(createdSurvey)
            return result
          }}
          onUpdate={this.props.firebase.updateSurvey}
          modifiedCallback={this.loadSurveys}
          toggle={this.handleCloseModal}
          cancel={this.handleCloseModal}
          resource={selectedSurvey || {}}
          isOpen={modalOpen}
          uiSchema={_surveyUISchema}
          dataSchema={this.getSurveySchema()}
          title={selectedSurvey?.id ? 'Muokkaa kyselyä' : 'Uusi kysely'}
        />

        <Dialog open={activateDialogOpen} onClose={this.handleCloseActivateDialog}>
          <DialogTitle>Aktivoi kysely</DialogTitle>
          <DialogContent>
            <Typography>
              Haluatko aktivoida kyselyn "{surveyToActivate?.name}"?
            </Typography>
            {surveyToActivate?.startDate && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Kyselylle on asetettu aloituspäivä: {moment(surveyToActivate.startDate).format('D.M.YYYY')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseActivateDialog}>
              Peruuta
            </Button>
            {surveyToActivate?.startDate && (
              <Button
                color="info"
                onClick={this.handleScheduleSurvey}
              >
                <ScheduleIcon fontSize="small" style={{ marginRight: 4 }} />
                Aloita aikataulun mukaisesti ({moment(surveyToActivate.startDate).format('D.M.YYYY')})
              </Button>
            )}
            <Button
              color="success"
              onClick={this.handleActivateNow}
            >
              <PlayArrowIcon fontSize="small" style={{ marginRight: 4 }} />
              Aloita kysely nyt
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

function SurveysContainer(props) {
  const navigate = useNavigate()
  return <SurveysContainerClass {...props} navigate={navigate} />
}

export default withAuthentication(withFirebase(SurveysContainer))
